import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getFirestore,
  type Firestore,
  connectFirestoreEmulator,
  initializeFirestore,
  CACHE_SIZE_UNLIMITED,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getAuth, type Auth, connectAuthEmulator } from 'firebase/auth';
import { getStorage, type FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, type Functions, connectFunctionsEmulator } from 'firebase/functions';

/**
 * Firebase configuration interface
 */
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  /** Cloud Functions region — must match the deployed functions region (e.g. 'europe-west1') */
  functionsRegion?: string;
}

/**
 * Firebase instances
 */
export interface FirebaseInstances {
  app: FirebaseApp;
  db: Firestore;
  auth: Auth;
  storage: FirebaseStorage;
  functions?: Functions; // Optional - not all apps need Functions
}

let firebaseInstances: FirebaseInstances | null = null;

/**
 * Initialize Firebase with configuration from environment variables
 * This function should be called once at app startup
 */
export function initializeFirebaseApp(
  config: FirebaseConfig,
  isDev: boolean = false,
  /** Keep Firestore data + pending writes in IndexedDB so the app works offline (team PWA). */
  options: { offline?: boolean } = {}
): FirebaseInstances {
  if (firebaseInstances) {
    return firebaseInstances;
  }

  const app = initializeApp(config);

  // Initialize Firestore with CACHE_SIZE_UNLIMITED in dev (matches elka-manager config)
  // This prevents the "INTERNAL ASSERTION FAILED: Unexpected state" error
  const db = options.offline
    ? initializeFirestore(app, {
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
      })
    : isDev
      ? initializeFirestore(app, {
          cacheSizeBytes: CACHE_SIZE_UNLIMITED,
        })
      : getFirestore(app);

  const auth = getAuth(app);

  // In dev, use default storage (emulator); in prod, use explicit gs:// bucket to avoid
  // landing on the wrong bucket (e.g. .firebasestorage.app vs .appspot.com).
  const storage = isDev
    ? getStorage(app)
    : config.storageBucket
      ? getStorage(app, `gs://${config.storageBucket}`)
      : getStorage(app);

  // Initialize Functions pointing to the configured region so client calls never
  // land in the wrong region (would get 404s or cold-start mismatches).
  let functions: Functions | undefined;
  try {
    functions = getFunctions(app, config.functionsRegion || 'europe-west1');
  } catch (error) {
    console.warn('⚠️ Firebase Functions not available:', error);
  }

  // Connect to emulators in development mode
  if (isDev) {
    try {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, 'localhost', 8080);
      if (functions) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      connectStorageEmulator(storage, 'localhost', 9199);
      console.log('🔧 Firebase Emulators connected');
    } catch (error) {
      console.warn('⚠️ Failed to connect to emulators:', error);
    }
  }

  firebaseInstances = { app, db, auth, storage, functions };
  return firebaseInstances;
}

/**
 * Get Firebase instances (must be initialized first)
 */
export function getFirebaseInstances(): FirebaseInstances {
  if (!firebaseInstances) {
    throw new Error('Firebase not initialized. Call initializeFirebaseApp first.');
  }
  return firebaseInstances;
}

// Export individual instances for convenience
export const getAuthInstance = () => getFirebaseInstances().auth;
